import { Button, Checkbox } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../components/base/BasicDialog";
import { formatCurrency } from "../../components/common/PriceDisplay";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import {
    useInvestments,
    useProductStatuses,
    useProductTypes,
    useProducts,
} from "../../services/productsService";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../utils";
import { ProductStatusText } from "../ProductsPage/ProductStatusText";
import { AUTOCOMPLETE_FIELD_TYPE } from "../../helpers/constants";

export function SelectProductsDialog(props) {
    const { t } = useTranslation();
    const getLocalizedProperty = useLocalizedProperty();

    const [localSelectedProductIds, setLocalSelectedProductIds] = useState(
        props.getFormProductIds(),
    );

    const [productsDataLocal, setProductsDataLocal] = useState([]);
    const [productsCountLocal, setProductsCountLocal] = useState(0);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const productSearchParams = useMemo(() => {
        const params = new URLSearchParams(pagination.searchParams);
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize]);

    const { data: productsData, isLoading } = useProducts(productSearchParams);

    useEffect(() => {
        if (productsData?.results) {
            const formatted = productsData.results.map((item) => {
                const pricing = getCalculatedPricingForProduct(item);
                return {
                    id: item.id,
                    product_number: item.product_number,
                    type: item.type,
                    investment: item.investment.investment_name_sales,
                    footage: item.footage,
                    status: {
                        id: item.status.id,
                        status_name: item.status.status_name,
                        status_name_eng: item.status.status_name_eng,
                    },
                    offer_price_gross: pricing.listed.gross,
                    offer_price_per_sqm: pricing.listed.gross_per_sqm,
                    note: item.note,
                };
            });
            setProductsDataLocal(formatted);
            setProductsCountLocal(productsData.count);
        }
    }, [productsData]);

    const refreshSelectedProductIds = () => {
        const currentIds = props.getFormProductIds();
        setLocalSelectedProductIds(currentIds);
    };

    const { data: types } = useProductTypes(new URLSearchParams(), {
        select: (data) =>
            data.map((item) => ({
                id: item.id,
                label: getLocalizedProperty(item, "name"),
            })),
    });

    const { data: productStatusesOptions } = useProductStatuses(new URLSearchParams(), {
        select: (data) =>
            data.results.map((item) => ({
                value: item.status_name_eng,
                label: t(`products.status.${item.status_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("lead.leadForm.allProducts")}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            <LoaderWrapper showLoader={isLoading && !productsDataLocal.length}>
                <FilterTableWrapper
                    data={productsDataLocal}
                    countRecords={productsCountLocal}
                    localPagination={pagination}
                    filtersOpts={{
                        showCleanFilterIcon: true,
                    }}
                    tableStyle={{
                        maxHeight: "50vh",
                        overflow: "auto",
                    }}
                    onSearchFiltersChange={pagination.handleChangeSearchParams}
                    fieldsConfig={[
                        {
                            getValueCallback: (row) => (
                                <Checkbox
                                    checked={!!localSelectedProductIds.find((id) => id === row.id)}
                                    onChange={(e) => {
                                        if (e.currentTarget.checked) {
                                            props.handleSelectProductId(row.id);
                                        } else {
                                            props.handleUnselectProductId(row.id);
                                        }
                                        refreshSelectedProductIds();
                                    }}
                                />
                            ),
                        },
                        {
                            name: "product_number",
                            getValueCallback: (row) => row.product_number,
                            label: t("products.columns.number"),
                            filterType: "text",
                        },
                        {
                            name: "type_id",
                            other_export_field_name: "type",
                            getValueCallback: (rowData) =>
                                rowData.type ? getLocalizedProperty(rowData.type, "name") : "",
                            label: t("products.columns.productType"),
                            filterType: AUTOCOMPLETE_FIELD_TYPE,
                            minWidth: 120,
                            width: 120,
                            fieldConfig: {
                                optionLabelKey: "label",
                                options: types || [],
                                multiple: true,
                                isObjectOption: true,
                                disableClearable: false
                            },
                        },
                        {
                            name: "investment",
                            getValueCallback: (row) => row.investment,
                            label: t("products.columns.investmentName"),
                            filterType: "select",
                            fieldConfig: {
                                optionKey: "value",
                                optionLabel: "label",
                                options: investments || [],
                            },
                        },
                        {
                            name: "footage",
                            getValueCallback: (row) => row.footage,
                            label: t("products.columns.footage"),
                            // @ts-ignore
                            filterType: "numeric_range",
                        },
                        {
                            name: "status",
                            getValueCallback: (row) => <ProductStatusText status={row.status} />,
                            label: t("products.columns.productStatus"),
                            filterType: "select",
                            fieldConfig: {
                                options: productStatusesOptions || [],
                                optionKey: "value",
                                optionLabel: "label",
                            },
                        },
                        {
                            name: "offer_price_gross",
                            getValueCallback: (row) => formatCurrency(row.offer_price_gross),
                            label: t("products.columns.priceGross"),
                        },
                        {
                            name: "offer_price_per_sqm",
                            getValueCallback: (row) => formatCurrency(row.offer_price_per_sqm),
                            label: t("products.columns.priceGrossM2"),
                        },
                        {
                            name: "note",
                            getValueCallback: (row) => row.note,
                            label: t("products.columns.note"),
                            filterType: "text",
                        },
                    ]}
                />
            </LoaderWrapper>
            <Button variant="contained" sx={{ mt: 2 }} onClick={props.onClose}>
                {t("lead.leadForm.add")}
            </Button>
        </BasicDialog>
    );
}
